import type { AuthenticatedUser, ISchedule } from "@gettactic/api";
import { canScheduleOthers } from "../permissions";

/**
 * Determines whether the current user is allowed to see/use the
 * online meeting "Join" link on a reservation.
 *
 * The link must only be exposed to:
 *  - workspace admins (users who can schedule on behalf of others),
 *  - the meeting organizer,
 *  - explicit attendees (users present in the reservation's slots).
 *
 * This is required for reservations synced from external calendars
 * (e.g. Outlook/Teams) where the room is shared with the org but the
 * meeting itself is private to invitees.
 */
export function canAccessJoinLink(
  schedule: Pick<ISchedule, "organizer_id" | "slots"> | null | undefined,
  authenticatedUser: AuthenticatedUser
): boolean {
  if (!schedule) return false;

  if (canScheduleOthers(authenticatedUser)) return true;

  const myId = authenticatedUser?.user?.id;
  if (!myId) return false;

  if (schedule.organizer_id === myId) return true;

  return (schedule.slots ?? []).some((slot) => slot.user?.id === myId);
}
